import { AppSettings } from "@/AppSettings";
import { ConfigurationSourceDataEvent, ConfigurationSourceDataMap } from "@/api/dtos";
import ConfigurationSourceDataBulkEdit from "@/components/forms/forms/source-data-configuration/ConfigurationSourceDataBulkEdit";
import { ConfigurationSourceDataEventBulkEditSettings } from "@/components/forms/forms/source-data-configuration/ConfigurationSourceDataEventForm";
import { ConfigurationSourceDataFormEventDetails } from "@/components/forms/forms/source-data-configuration/ConfigurationSourceDataFormDetails";
import MetadataContext from "@/contexts/data/MetadataContext";
import TrialContext from "@/contexts/data/TrialContext";
import { copyConfigurationSourceDataEventSettings } from "@/hooks/form/configuration/useEventConfiguration";
import useEventConfigurations from "@/hooks/form/configuration/useEventConfigurations";
import useDebouncedValue from "@/hooks/utility/useDebouncedValue";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, Button, List, Paper, styled } from "@mui/material";
import { useScopedField } from "@ngt/forms-core";
import { FC, useCallback, useContext, useState } from "react";

export const ActionButton = styled(Button)(() => ({ 
    borderRadius: 0,
    fontSize: 16
}));

export const ActionButtonBox = styled(Box)(() => ({ 
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-between'
}));

export const ActionBox = styled(Box)(({ theme }) => ({
    top: 0, 
    bottom: 0, 
    height: '48px', 
    display: 'flex', 
    marginRight: theme.spacing(-2)
}));

const ConfigurationSourceDataEvents: FC = () => {

    const { data: trial } = useContext(TrialContext);
    const { data: metadata } = useContext(MetadataContext);
    const { state: { value: configuration } } = useScopedField<ConfigurationSourceDataMap>('configuration', { value: true });
    const { state: { value: trialConfiguration } } = useScopedField<ConfigurationSourceDataMap>('trialConfiguration', { value: true });


    const {
        state: debouncedConfiguration,
        doNotDebounceNextUpdate: doNoteDebounceNextConfigurationUpdate
    } = useDebouncedValue(configuration, 500);

    const {
        state: debouncedTrialConfiguration,
        doNotDebounceNextUpdate: doNoteDebounceNextTrialConfigurationUpdate
    } = useDebouncedValue(trialConfiguration, 500);

    const onLinkUpdate = useCallback(() => {
        doNoteDebounceNextConfigurationUpdate();
        doNoteDebounceNextTrialConfigurationUpdate();
    }, [doNoteDebounceNextConfigurationUpdate, doNoteDebounceNextTrialConfigurationUpdate])

    const eventData = useEventConfigurations(
        metadata,
        debouncedTrialConfiguration ?? undefined,
        debouncedConfiguration ?? undefined,
        trial,
        debouncedConfiguration?.configuration?.id
    );

    const [bulkEdit, setBulkEdit] = useState(false);

    const onBulkEditCancel = useCallback(() => {
        setBulkEdit(false);
    }, [setBulkEdit])

    if (!eventData || eventData.length === 0) {
        return (
            <Alert
                icon={<FontAwesomeIcon icon={AppSettings.icons.alertWarning} />}
                severity="warning"
            >
                <AlertTitle>No Events</AlertTitle>
                No events were found.
            </Alert> 
        )
    }

    if (bulkEdit) {
        return (
            <ConfigurationSourceDataBulkEdit
                data={eventData}
                settings={ConfigurationSourceDataEventBulkEditSettings}
                onLinkUpdate={onLinkUpdate}
                onCancel={onBulkEditCancel}
            />
        )
    }
    

    return (
        <>
            <Box
                sx={{
                    my: 2,
                    display: 'flex',
                    gap: 3,
                    alignItems: 'center'
                }}
            >
                <Button
                    variant="outlined"
                    color="primary"
                    sx={{
                        width: 120,
                        minWidth: 120,
                        maxWidth: 120,
                        minHeight: 52,
                        ml: 'auto'
                    }}
                    onClick={() => setBulkEdit(true)}
                >
                    Bulk Edit
                </Button>
            </Box>
            <Paper>
                <List disablePadding>
                    {
                        eventData.map((x, i, a) => (
                            <ConfigurationSourceDataFormEventDetails
                                key={x.studyEventOid}
                                eventData={x}
                                divider={(i + 1) < a.length}
                            />
                        ))
                    }
                </List>
            </Paper>
        </>
    )
};

export default ConfigurationSourceDataEvents;